export const METRONOME_CALLBACK_INTERVAL_MS = 35;
export const METRONOME_SCHEDULE_AHEAD_SECONDS = 0.32;
export const METRONOME_MIN_LEAD_SECONDS = 0.025;

export type CountInSchedule = {
  startTime: number;
  beatTimes: number[];
  recordStartTime: number;
  secondsPerBeat: number;
  safeLeadTime: number;
};

export function safeAudioLeadSeconds(baseLatency = 0, outputLatency = 0): number {
  const measuredPathLatency = Math.max(0, baseLatency) + Math.max(0, outputLatency);
  return Math.min(0.6, Math.max(0.35, 0.18 + measuredPathLatency * 2));
}

export function buildCountInSchedule(options: {
  now: number;
  bpm: number;
  beats?: number;
  audioState: AudioContextState | "unavailable";
  baseLatency?: number;
  outputLatency?: number;
}): CountInSchedule {
  if (options.audioState !== "running") throw new Error("Audio output is not ready for count-in");
  const beats = Math.max(1, Math.round(options.beats ?? 4));
  const secondsPerBeat = 60 / Math.max(1, options.bpm);
  const safeLeadTime = safeAudioLeadSeconds(options.baseLatency, options.outputLatency);
  const startTime = options.now + safeLeadTime;
  const beatTimes = Array.from({ length: beats }, (_, index) => startTime + index * secondsPerBeat);
  return {
    startTime,
    beatTimes,
    recordStartTime: startTime + beats * secondsPerBeat,
    secondsPerBeat,
    safeLeadTime,
  };
}

export type LookaheadPlan = {
  scheduled: { beatIndex: number; time: number }[];
  nextBeatIndex: number;
  skippedBeatCount: number;
};

export function planMetronomeLookahead(options: {
  now: number;
  sessionStartTime: number;
  secondsPerBeat: number;
  nextBeatIndex: number;
  scheduleAheadSeconds?: number;
  minimumLeadSeconds?: number;
}): LookaheadPlan {
  const horizon = options.now + (options.scheduleAheadSeconds ?? METRONOME_SCHEDULE_AHEAD_SECONDS);
  const deadline = options.now + (options.minimumLeadSeconds ?? METRONOME_MIN_LEAD_SECONDS);
  let nextBeatIndex = Math.max(0, options.nextBeatIndex);
  let skippedBeatCount = 0;
  const beatTime = (index: number) => options.sessionStartTime + index * options.secondsPerBeat;

  while (beatTime(nextBeatIndex) < deadline) {
    nextBeatIndex += 1;
    skippedBeatCount += 1;
  }

  const scheduled: { beatIndex: number; time: number }[] = [];
  while (beatTime(nextBeatIndex) <= horizon) {
    scheduled.push({ beatIndex: nextBeatIndex, time: beatTime(nextBeatIndex) });
    nextBeatIndex += 1;
  }
  return { scheduled, nextBeatIndex, skippedBeatCount };
}

export function isAudioBoundaryArmed(currentAudioTime: number, boundaryAudioTime: number | null): boolean {
  return boundaryAudioTime !== null && currentAudioTime >= boundaryAudioTime;
}

export function countInNumberAtAudioTime(schedule: CountInSchedule, currentAudioTime: number): number | null {
  if (currentAudioTime >= schedule.recordStartTime) return null;
  if (currentAudioTime <= schedule.startTime) return schedule.beatTimes.length;
  const elapsedBeats = Math.floor(
    (currentAudioTime - schedule.startTime + schedule.secondsPerBeat * 1e-7) /
      schedule.secondsPerBeat,
  );
  return Math.max(1, schedule.beatTimes.length - elapsedBeats);
}
